// Tjänstekatalogen (designen Admin-Katalog): rubriken med status, knapparna
// för att testa och publicera, och flikarna (ADR 0064).
import { useState } from 'react';
import { Link, NavLink, Outlet, useOutletContext } from 'react-router';
import { useMe } from '../../auth/auth.js';
import { felText, useKatalog, type Katalog as KatalogData } from '../../katalog/useKatalog.js';
import { Sida } from '../../layout/Sida.js';
import type { ServiceCatalog } from '../../katalog/sammanfattning.js';

export interface KatalogKontext {
  data: KatalogData;
  katalog: ServiceCatalog;
  /** Bara ägaren får ändra. */
  kanAndra: boolean;
}

export const useKatalogKontext = () => useOutletContext<KatalogKontext>();

const FLIKAR = [
  { till: '/katalog', namn: 'Tjänster', end: true },
  { till: '/katalog/regler', namn: 'Regler för automatiska offerter', end: false },
  { till: '/katalog/villkor', namn: 'Villkor och texter', end: false },
  { till: '/katalog/versioner', namn: 'Versioner', end: false },
];

function Status({ data }: { data: KatalogData }) {
  if (!data.utkast)
    return <span className="chip chip-gron">Publicerad version {data.publicerad?.version}</span>;
  if (!data.publicerad) return <span className="chip chip-gul">Inte publicerad</span>;
  if (data.andringar === null || data.andringar === 0) return null;
  return (
    <span className="chip chip-gul">
      {data.andringar === 1
        ? '1 ändring ej publicerad'
        : `${data.andringar} ändringar ej publicerade`}
    </span>
  );
}

export function Katalog() {
  const me = useMe();
  const data = useKatalog();
  const [publicerad, setPublicerad] = useState<number | null>(null);
  const kanAndra = me.user.role === 'owner';

  if (data.laddar) {
    return (
      <Sida titel="Tjänstekatalog">
        <p role="status">Hämtar katalogen …</p>
      </Sida>
    );
  }
  if (data.fel) {
    return (
      <Sida titel="Tjänstekatalog">
        <div className="meddelande" role="alert">
          Katalogen gick inte att hämta. Ladda om sidan och försök igen.
        </div>
      </Sida>
    );
  }
  if (!data.katalog) {
    return (
      <Sida titel="Tjänstekatalog">
        <div className="kort kom-igang-kort">
          <h2>Ni har ingen tjänstekatalog än</h2>
          <p>Börja med en startmall eller en tom katalog.</p>
          <Link className="btn btn-p" to="/kom-igang">
            Välj startmall
          </Link>
        </div>
      </Sida>
    );
  }

  async function publicera() {
    const modell = await data.publicera.mutateAsync();
    setPublicerad(modell.version);
  }

  return (
    <Sida titel="Tjänstekatalog" bredvid={<Status data={data} />}>
      <div className="katalog-huvud">
        <p className="ingress">
          AI väljer bara tjänster härifrån. Priserna räknas alltid exakt enligt era regler.
        </p>
        <div className="knappar">
          <Link className="btn btn-s" to="/testlage">
            Testa en förfrågan
          </Link>
          {kanAndra && data.utkast && (
            <button
              type="button"
              className="btn btn-p"
              disabled={data.publicera.isPending}
              onClick={() => void publicera().catch(() => undefined)}
            >
              {data.publicera.isPending
                ? 'Publicerar …'
                : `Publicera version ${data.utkast.version}`}
            </button>
          )}
        </div>
      </div>
      <div className="levande" aria-live="polite">
        {publicerad !== null && !data.publicera.isError && (
          <div className="kvitto" role="status">
            Version {publicerad} är publicerad. Nya offerter räknas på den.
          </div>
        )}
      </div>
      {data.publicera.isError && (
        <div className="meddelande" role="alert">
          {felText(data.publicera.error)}
        </div>
      )}
      {!kanAndra && (
        <div className="kort notis" role="note">
          Bara byråns ägare kan ändra katalogen. Du kan se allt och testa förfrågningar.
        </div>
      )}
      <nav className="flikar" aria-label="Katalog">
        {FLIKAR.map((f) => (
          <NavLink key={f.till} to={f.till} end={f.end} className="flik">
            {f.namn}
          </NavLink>
        ))}
      </nav>
      <Outlet context={{ data, katalog: data.katalog, kanAndra } satisfies KatalogKontext} />
    </Sida>
  );
}
